/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './AppTooltip.css';

type TooltipPlacement = 'top' | 'bottom';

interface TooltipPosition {
  top: number;
  left: number;
  arrowLeft: number;
  placement: TooltipPlacement;
}
type TooltipChildProps = React.HTMLAttributes<HTMLElement> & {
  'aria-describedby'?: string;
};

export interface AppTooltipProps {
  /** Uygulamanın seçili dildeki görünen adı. */
  label: string;
  /** Core registry tarafından sağlanan kısa kurumsal açıklama. */
  description?: string;
  /** Sıralama/düzenleme gibi durumlarda tooltip davranışını kapatır. */
  disabled?: boolean;
  /** Hover açılış gecikmesi; klavye odağında beklenmez. */
  hoverDelayMs?: number;
  children: React.ReactElement<TooltipChildProps>;
}

const VIEWPORT_MARGIN = 12;
const TOOLTIP_GAP = 10;

function mergeDescribedBy(existing: string | undefined, tooltipId: string): string {
  return [existing, tooltipId].filter(Boolean).join(' ');
}

export function AppTooltip({
  label,
  description,
  disabled = false,
  hoverDelayMs = 280,
  children,
}: AppTooltipProps) {
  const reactId = useId();
  const tooltipId = `qp-app-tooltip-${reactId.replace(/:/g, '')}`;
  const anchorRef = useRef<HTMLElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<TooltipPosition | null>(null);

  const resolvedLabel = label.trim();
  const resolvedDescription = description?.trim() ?? '';
  const canShow = !disabled && Boolean(resolvedLabel && resolvedDescription);

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  const hide = useCallback(() => {
    clearHoverTimer();
    setOpen(false);
    setPosition(null);
  }, [clearHoverTimer]);

  const show = useCallback((anchor: HTMLElement, immediate: boolean) => {
    if (!canShow) return;
    clearHoverTimer();
    anchorRef.current = anchor;
    const reveal = () => setOpen(true);
    if (immediate) reveal();
    else hoverTimerRef.current = setTimeout(reveal, hoverDelayMs);
  }, [canShow, clearHoverTimer, hoverDelayMs]);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    const tooltip = tooltipRef.current;
    if (!anchor || !tooltip) return;

    const anchorRect = anchor.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const availableAbove = anchorRect.top - VIEWPORT_MARGIN;
    const placement: TooltipPlacement = availableAbove >= tooltipRect.height + TOOLTIP_GAP
      ? 'top'
      : 'bottom';

    const unclampedLeft = anchorRect.left + (anchorRect.width / 2) - (tooltipRect.width / 2);
    const maxLeft = Math.max(VIEWPORT_MARGIN, window.innerWidth - tooltipRect.width - VIEWPORT_MARGIN);
    const left = Math.min(Math.max(unclampedLeft, VIEWPORT_MARGIN), maxLeft);
    const rawTop = placement === 'top'
      ? anchorRect.top - tooltipRect.height - TOOLTIP_GAP
      : anchorRect.bottom + TOOLTIP_GAP;
    const maxTop = Math.max(VIEWPORT_MARGIN, window.innerHeight - tooltipRect.height - VIEWPORT_MARGIN);
    const top = Math.min(Math.max(rawTop, VIEWPORT_MARGIN), maxTop);
    const anchorCenter = anchorRect.left + (anchorRect.width / 2);
    const arrowLeft = Math.min(
      Math.max(anchorCenter - left, 14),
      Math.max(14, tooltipRect.width - 14),
    );

    setPosition({ top, left, arrowLeft, placement });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(updatePosition);
    return () => cancelAnimationFrame(frame);
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const reposition = () => updatePosition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => () => clearHoverTimer(), [clearHoverTimer]);

  useEffect(() => {
    if (!canShow) hide();
  }, [canShow, hide]);

  const childProps = children.props;
  const enhancedChild = React.cloneElement(children, {
    'aria-describedby': canShow
      ? mergeDescribedBy(childProps['aria-describedby'], tooltipId)
      : childProps['aria-describedby'],
    onMouseEnter: (event: React.MouseEvent<HTMLElement>) => {
      childProps.onMouseEnter?.(event);
      show(event.currentTarget, false);
    },
    onMouseLeave: (event: React.MouseEvent<HTMLElement>) => {
      childProps.onMouseLeave?.(event);
      hide();
    },
    onFocus: (event: React.FocusEvent<HTMLElement>) => {
      childProps.onFocus?.(event);
      show(event.currentTarget, true);
    },
    onBlur: (event: React.FocusEvent<HTMLElement>) => {
      childProps.onBlur?.(event);
      hide();
    },
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
      childProps.onKeyDown?.(event);
      if (event.key === 'Escape') hide();
    },
  });

  return (
    <>
      {enhancedChild}
      {open && typeof document !== 'undefined' && createPortal(
        <div
          ref={tooltipRef}
          id={tooltipId}
          className="qp-app-tooltip"
          role="tooltip"
          data-placement={position?.placement ?? 'top'}
          data-ready={position ? 'true' : 'false'}
          style={position ? {
            top: `${position.top}px`,
            left: `${position.left}px`,
            '--qp-app-tooltip-arrow-left': `${position.arrowLeft}px`,
          } as React.CSSProperties : undefined}
        >
          <span className="qp-app-tooltip-title">{resolvedLabel}</span>
          <span className="qp-app-tooltip-description">{resolvedDescription}</span>
          <span className="qp-app-tooltip-arrow" aria-hidden />
        </div>,
        document.body,
      )}
    </>
  );
}
